import MomentUtils from "@date-io/moment";
import ScopedCssBaseline from "@material-ui/core/ScopedCssBaseline";
import {
	jssPreset,
	StylesProvider,
	ThemeProvider,
} from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { CSSProperties } from "@material-ui/styles";
import AuthView from "Components/Auth/AuthView";
import { getTokenFromUrlParams } from "Components/Auth/helpers/getTokenFromUrlParams";
import { PRESENTAION_ELEMENT_ID } from "Components/Modal/Modal";
import { color, useMuiTheme } from "Configs/theme";
import { create as createJss } from "jss";
import SnackbarProvider from "Providers/SnackbarProvider";
import { TranslationProvider } from "Providers/TranslationProvider";
import React, { createContext, FC, Fragment, useContext, useRef } from "react";
import { createDndContext, DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import ShadowDomContainer from "./ShadowDomContainer";
import { WindowSizeProvider } from "./WindowSizeProvider";

interface App {
	isStandaloneApp: boolean;
	isNative: boolean;
	shadowRoot?: ShadowRoot;
	mountPoint?: HTMLDivElement;
	scrollElement?: HTMLElement;
	token?: string | null;
}

const AppContext = createContext<App>({
	isStandaloneApp: false,
	isNative: getIsNative(),
});

function getIsNative() {
	return new URLSearchParams(window.location.search).get("native") === "1";
}

interface Props {
	isStandaloneApp: boolean;
	shadowRoot: ShadowRoot;
	mountPoint: HTMLDivElement;
	reactRoot: HTMLDivElement;
	token: string | null;
	initialDate: string | null;
	initialView: string | null;
}

const dndContext = createDndContext(HTML5Backend);

const AppProvider: FC<Props> = (props) => {
	const muiTheme = useMuiTheme(props.shadowRoot);
	const manager = useRef(dndContext);
	const isNative = getIsNative();
	const jssRef = useRef(
		createJss({
			...jssPreset(),
			insertionPoint: props.reactRoot,
		})
	);

	const app: App = {
		isNative,
		isStandaloneApp: props.isStandaloneApp,
		shadowRoot: props.shadowRoot,
		mountPoint: props.mountPoint,
		scrollElement: props.isStandaloneApp
			? document.documentElement
			: props.mountPoint,
		token: props.token || getTokenFromUrlParams(),
	};

	return (
		<Fragment>
			<AppContext.Provider value={app}>
				<ScopedCssBaseline style={baselineCss}>
					<StylesProvider jss={jssRef.current}>
						<WindowSizeProvider isNative={isNative}>
							<ThemeProvider theme={muiTheme}>
								<DndProvider manager={manager.current.dragDropManager!}>
									<MuiPickersUtilsProvider utils={MomentUtils}>
										<TranslationProvider>
											<SnackbarProvider>
												<ShadowDomContainer>
													<AuthView 
														initialDate={props.initialDate || undefined}
														initialView={props.initialView || undefined}
													/>
												</ShadowDomContainer>
											</SnackbarProvider>
										</TranslationProvider>
									</MuiPickersUtilsProvider>
								</DndProvider>
							</ThemeProvider>
						</WindowSizeProvider>
					</StylesProvider>
				</ScopedCssBaseline>
			</AppContext.Provider>
			<div id={PRESENTAION_ELEMENT_ID} />
		</Fragment>
	);
};

export default AppProvider;

export function useApp() {
	return useContext(AppContext);
}

const baselineCss: CSSProperties = {
	fontFamily: "Montserrat",
	lineHeight: 1.43,
	color: color.text,
	display: "flex",
	flexDirection: "column",
	flex: 1,
	fontSize: "0.875rem",
	letterSpacing: "0.01071em",
};
